import { inject } from 'mobx-react'
import React from 'react'
import { IStores } from 'src/Stores'
import WorkflowInbox from './WorkflowInbox'
import { useParams } from 'react-router-dom'
import { IEpisodeOfCare, ICMS1500, ILocation, ISchedule } from 'src/Definitions'
import { IProviderDto } from 'src/generated_client'
import { DropdownOption } from 'src/viewModels/DropdownOption'
import ReceiveClaim from 'src/ReceiveClaim/ReceiveClaim'
import { AttachMoney } from '@mui/icons-material'
import { getClaimInboxItem, getDefaultClaimInboxItem } from 'src/services/WorkflowService'
import { ClaimInboxItem } from 'src/viewModels/workflow/ClaimInboxItem'
import { InboxItemType } from 'src/utils/InboxItemType'
import { InjectedViewClaim } from 'src/viewClaim/viewClaim'
import { IClaimFormValues } from 'src/ReceiveClaim/ClaimFormValues'
import { NextAssociatedItemSummary } from 'src/viewModels/workflow/NextAssociatedItemSummary'
import { CircularProgress } from '@mui/material'

class ReceiveClaimParams {
  episodeOfCareId: string
  quoteId: string
  claimId: string
  appealType: string
}

class matchParams {
  params: ReceiveClaimParams
  isExact: boolean
  path: string
  url: string
}

interface IClaimInboxProps {
  claimId?: string
  inboxId?: string
  closeModal?: () => void
  episodeOfCare?: IEpisodeOfCare
  isLoading?: boolean
  isModalOpen?: boolean
  isCMS1500Loading?: boolean
  isCurrentOrganizationSurgical?: boolean
  isEpisodeOfCareLoading?: boolean
  isQuoteLoading?: boolean
  loadEpisodeOfCare?: (episodeOfCareId: string) => Promise<void>
  loadClaim?: (claimId: string) => Promise<void>
  rerouteToPath?: (path: string) => void
  saveCMS1500?: (cms1500: ICMS1500) => Promise<NextAssociatedItemSummary>
  selectedCMS1500?: ICMS1500
  selectedEpisodeOfCare?: IEpisodeOfCare
  selectedLocation?: ILocation
  selectedSchedule?: ISchedule
  setSelectedCMS1500?: (cms1500: ICMS1500 | undefined) => void
  setSelectedEpisodeOfCare?: (episodeOfCare: IEpisodeOfCare | undefined) => void
  setSelectedLocation?: (location?: ILocation) => void
  setSelectedSchedule?: (schedule?: ISchedule) => void
  checkDuplicateBills?: (cms1500: ICMS1500) => Promise<void>
  duplicateCMS1500?: ICMS1500
  openModal?: () => void
  scheduleItems?: DropdownOption[]
  locationItems?: DropdownOption[]
  setSelectedScheduleById?: (id: string) => Promise<void>
  getScheduledOptions?: () => void
  openDialogWithSchedule?: (schedule: ISchedule | undefined) => void
  openProviderLookupDialog?: () => void
  getLocationById?: (locationId: string) => void
  isProviderLookupDialogOpen?: boolean
  showContractModal?: (provider: IProviderDto) => void
  setReturn?: () => void
  closeTab?: () => void
  setUpdateParentAfterStaticAction?: (parentfunction?: () => Promise<ICMS1500>) => void
}

class ClaimInbox extends React.Component<IClaimInboxProps> {
  public state = {
    headerInfo: undefined as unknown as ClaimInboxItem,
    isLoading: true,
    eocId: '',
    claimFormValues: undefined as unknown as IClaimFormValues,
  }

  public async componentDidMount(): Promise<void> {
    await this.loadClaimData()
  }

  public async componentDidUpdate(prevProps: IClaimInboxProps): Promise<void> {
    if (
      prevProps.claimId !== this.props.claimId ||
      prevProps.inboxId !== this.props.inboxId
    ) {
      this.setState({
        isLoading: true,
        headerInfo: undefined as unknown as ClaimInboxItem,
        eocId: '',
      })
      await this.loadClaimData()
    }
  }

  public componentWillUnmount(): void {
    // Clear store data when unmounting to prevent flashing old data
    this.props.setSelectedCMS1500?.(undefined)
    this.props.setSelectedEpisodeOfCare?.(undefined)
    this.props.setSelectedLocation?.(undefined)
  }

  private async loadClaimData(): Promise<void> {
    if (this.props.claimId && this.props.inboxId) {
      await getClaimInboxItem(this.props.claimId, this.props.inboxId)
        .then(async (response: any) => {
          this.setState({
            headerInfo: response,
            eocId: response.associatedEpisodeOfCareId,
            isLoading: false,
          })
        })
        .catch(() => {
          this.setState({ isLoading: false })
        })
    } else if (this.props.claimId) {
      await getDefaultClaimInboxItem(this.props.claimId)
        .then(async (response: any) => {
          this.setState({
            headerInfo: response,
            eocId: response.associatedEpisodeOfCareId,
            isLoading: false,
          })
        })
        .catch(() => {
          this.setState({ isLoading: false })
        })
    } else {
      this.setState({ isLoading: false })
    }
  }

  public renderClaim() {
    var match = new matchParams()
    match.params = new ReceiveClaimParams()
    match.params.episodeOfCareId = this.state.eocId
    match.params.claimId = this.props.claimId || ''

    if (
      this.state.headerInfo &&
      !this.state.headerInfo.restrictedToOutOfWorkflowActions &&
      !this.state.headerInfo.isDynamicItem
    ) {
      const ReceiveClaimComponent = ReceiveClaim as any
      return (
        <ReceiveClaimComponent
          key={this.props.claimId}
          checkDuplicateBills={this.props.checkDuplicateBills}
          closeModal={this.props.closeModal}
          duplicateCMS1500={this.props.duplicateCMS1500}
          episodeOfCare={this.props.episodeOfCare}
          isCMS1500Loading={this.props.isCMS1500Loading}
          isCurrentOrganizationSurgical={this.props.isCurrentOrganizationSurgical}
          isEpisodeOfCareLoading={this.props.isEpisodeOfCareLoading}
          isLoading={this.props.isLoading}
          isModalOpen={this.props.isModalOpen}
          loadClaim={this.props.loadClaim}
          loadEpisodeOfCare={this.props.loadEpisodeOfCare}
          openModal={this.props.openModal}
          rerouteToPath={this.props.rerouteToPath}
          saveCMS1500={this.props.saveCMS1500}
          selectedCMS1500={this.props.selectedCMS1500}
          selectedEpisodeOfCare={this.props.selectedEpisodeOfCare}
          selectedLocation={this.props.selectedLocation}
          selectedSchedule={this.props.selectedSchedule}
          setSelectedCMS1500={this.props.setSelectedCMS1500}
          setSelectedEpisodeOfCare={this.props.setSelectedEpisodeOfCare}
          setSelectedLocation={this.props.setSelectedLocation}
          setSelectedSchedule={this.props.setSelectedSchedule}
          scheduleItems={this.props.scheduleItems}
          locationItems={this.props.locationItems}
          setSelectedScheduleById={this.props.setSelectedScheduleById}
          getScheduledOptions={this.props.getScheduledOptions}
          openDialogWithSchedule={this.props.openDialogWithSchedule}
          openProviderLookupDialog={this.props.openProviderLookupDialog}
          getLocationById={this.props.getLocationById}
          isProviderLookupDialogOpen={this.props.isProviderLookupDialogOpen}
          showContractModal={this.props.showContractModal}
          setReturn={this.props.setReturn}
          closeTab={this.props.closeTab}
          match={match}
          workflowStyling={true}
          setGetClaimValuesForStaticAction={this.props.setUpdateParentAfterStaticAction}
        />
      )
    } else {
      const match = {
        params: {
          claimId: this.props.claimId,
          episodeOfCareId: this.state.eocId,
        },
      }
      return (
        <InjectedViewClaim
          key={this.props.claimId}
          workflowStyling={true}
          match={match}
        />
      )
    }
  }

  public saveClaim() {}

  public render() {
    if (this.state.isLoading || !this.state.headerInfo) {
      return (
        <div
          style={{
            position: 'fixed',
            display: 'flex',
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            cursor: 'pointer',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: '2',
          }}
        >
          <CircularProgress size={100} />
        </div>
      )
    }

    return this.state.eocId || this.props.claimId ? (
      <WorkflowInbox
        inboxType={InboxItemType.Claim}
        item={this.renderClaim()}
        headerInfo={this.state.headerInfo}
        icon={<AttachMoney />}
      />
    ) : (
      <div>No claim data available</div>
    )
  }
}

const InjectedClaimInbox = inject<
  IStores,
  IClaimInboxProps,
  Partial<IClaimInboxProps>,
  any
>((stores: IStores) => ({
  checkDuplicateBills: stores.cms1500.checkDuplicateBills,
  closeModal: stores.cms1500.closeDialog,
  duplicateCMS1500: stores.cms1500.duplicateCMS1500,
  episodeOfCare: stores.patientEdit.selectedEpisodeOfCare,
  getProviderSuggestions: stores.quotes.getProviderSuggestions,
  getTypesByCategory: stores.quotes.getTypesByCategory,
  isCMS1500Loading: stores.cms1500.isLoading,
  isCurrentOrganizationSurgical: stores.global.isCurrentOrganizationSurgical,
  isEpisodeOfCareLoading: stores.patientEdit.isLoading,
  isLoading: stores.quotes.isLoading,
  isModalOpen: stores.cms1500.isModalOpen,
  loadClaim: stores.cms1500.loadCMS1500,
  loadEpisodeOfCare: stores.patientEdit.getEpisodeOfCareById,
  openModal: stores.cms1500.openDialog,
  providers: stores.quotes.providers,
  ptTypes: stores.quotes.ptTypes,
  rerouteToPath: stores.global.rerouteToPath,
  saveCMS1500: stores.cms1500.saveCMS1500,
  selectedCMS1500: stores.cms1500.selectedCMS1500,
  selectedEpisodeOfCare: stores.patientEdit.selectedEpisodeOfCare,
  selectedLocation: stores.locations.selectedEditClaimLocation,
  selectedSchedule: stores.schedules.selectedSchedule,
  setSelectedCMS1500: stores.cms1500.setSelectedCMS1500,
  setSelectedEpisodeOfCare: stores.patientEdit.setSelectedEpisodeOfCare,
  setSelectedLocation: stores.locations.setSelectedEditClaimLocation,
  setSelectedSchedule: stores.schedules.setSelectedSchedule,
  scheduleItems: stores.patientEdit.scheduleItems,
  locationItems: stores.schedules.scheduleItems,
  setSelectedScheduleById: stores.schedules.setSelectedScheduleById,
  getScheduledOptions: stores.patientEdit.getScheduledOptions,
  openDialogWithSchedule: stores.schedules.openDialogWithSchedule,
  openProviderLookupDialog: stores.schedules.openClaimProviderLookupDialog,
  getLocationById: stores.locations.getLocationById,
  isProviderLookupDialogOpen: stores.schedules.isClaimProviderLookupDialogOpen,
  showContractModal: stores.cms1500.openProviderGroupContractDialog,
  setReturn: stores.global.setReturn,
  closeTab: stores.global.closeTab,
  setUpdateParentAfterStaticAction: stores.cms1500.setGetClaimValuesForStaticAction,
}))(ClaimInbox)

// Wrapper component to extract route params and pass them as props
const ClaimInboxWrapper: React.FC = () => {
  const params = useParams<{ claimId?: string; inboxId?: string }>()
  return (
    <InjectedClaimInbox
      key={`${params.claimId}-${params.inboxId}`}
      claimId={params.claimId}
      inboxId={params.inboxId}
    />
  )
}

export default ClaimInboxWrapper
